// components/CardPagosMercadoPago.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  useTheme,
  IconButton,
  Collapse,
  Tooltip,
  TablePagination,
  TableFooter
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircleOutline as MatchedIcon,
  ErrorOutline as UnmatchedIcon,
  Warning as WarningIcon,
  CompareArrows as CompareArrowsIcon,
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import CardExpandible from './CardExpandible';
import { getPagosDB } from '../services/pagos.service';
import { conciliarPagos } from '../services/mercadopago.service';
import moment from 'moment';

const formatNumber = (value) => {
  if (!value && value !== 0) return '';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';
  
  const [entero, decimal] = numValue.toFixed(2).split('.');
  const enteroFormateado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${enteroFormateado},${decimal}`;
};

const CardPagosMercadoPago = ({ 
  sucursalId,
  sucursalNombre,
  mpStoreId,
  desde, 
  hasta, 
  loading = false,
  triggerRecarga = 0,
  sucursal
}) => {
  const theme = useTheme();
  const [conciliacion, setConciliacion] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Estados de paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    if (sucursalId && mpStoreId && desde && hasta) {
      cargarConciliacion();
    }
  }, [sucursalId, mpStoreId, desde, hasta, triggerRecarga]);

  // Resetear página cuando cambia el tab
  useEffect(() => {
    setPage(0);
  }, [selectedTab]);

  const cargarConciliacion = async () => {
    setCargando(true);
    setError(null);
    
    try {
      const pagosDB = await getPagosDB(1, desde, hasta, sucursalId);
      
      if (!pagosDB || pagosDB.length === 0) {
        setError('No se encontraron pagos en la base de datos para este período');
        setCargando(false);
        return;
      }

      const response = await conciliarPagos({
        sucursalId: sucursalId,
        mpStoreId: mpStoreId,
        desde: desde,
        hasta: hasta,
        pagosDB: pagosDB,
        empresa: sucursal?.empresa
      });
      
      if (response.success) {
        setConciliacion(response.data);
        setResumen(response.resumen);
        setMetadata(response.metadata);
        setPage(0);
      } else {
        setError(response.error || 'Error al procesar la conciliación');
      }
    } catch (error) {
      console.error('[CardPagosMercadoPago] Error al cargar conciliación:', error);
      setError(error.message || 'Error al cargar los datos de conciliación');
    } finally {
      setCargando(false);
    }
  };

  const toggleExpandRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleTabChange = (tabIndex) => {
    setSelectedTab(tabIndex);
    setPage(0);
    setExpandedRows({});
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRows({});
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows({});
  };

  const getDatosTab = () => {
    if (!conciliacion) return [];
    switch(selectedTab) {
      case 0: return conciliacion.coincidencias || [];
      case 1: return conciliacion.soloDB || [];
      case 2: return conciliacion.soloMP || [];
      default: return [];
    }
  };

  const datosActuales = getDatosTab();
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - datosActuales.length) : 0;
  const datosPaginados = datosActuales.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Función para obtener el ícono de cada tab
  const getTabIcon = (tabIndex) => {
    switch(tabIndex) {
      case 0: return <CheckCircleIcon sx={{ fontSize: 20, color: '#2e7d32' }} />;
      case 1: return <InfoIcon sx={{ fontSize: 20, color: '#ed6c02' }} />;
      case 2: return <ErrorIcon sx={{ fontSize: 20, color: '#d32f2f' }} />;
      case 3: return <CompareArrowsIcon sx={{ fontSize: 20, color: '#1976d2' }} />;
      default: return null;
    }
  };

  // Función para obtener el color de fondo de cada tab
  const getTabBgColor = (tabIndex) => {
    switch(tabIndex) {
      case 0: return '#e8f5e9';
      case 1: return '#fff3e0';
      case 2: return '#fce4ec';
      case 3: return '#e3f2fd';
      default: return '#fafafa';
    }
  };

  // Función para obtener el color de borde de cada tab
  const getTabBorderColor = (tabIndex) => {
    switch(tabIndex) {
      case 0: return '#4caf50';
      case 1: return '#ff9800';
      case 2: return '#f44336';
      case 3: return '#1976d2';
      default: return '#e0e0e0';
    }
  };

  // Función para obtener el color de texto de cada tab
  const getTabTextColor = (tabIndex) => {
    switch(tabIndex) {
      case 0: return '#2e7d32';
      case 1: return '#ed6c02';
      case 2: return '#d32f2f';
      case 3: return '#1976d2';
      default: return '#424242';
    }
  };

  if (loading || cargando) {
    return (
      <CardExpandible title="Conciliación Mercado Pago" icon={PaymentIcon}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} sx={{ color: '#757575' }} />
        </Box>
      </CardExpandible>
    );
  }

  if (!sucursalId || !mpStoreId || !desde || !hasta) {
    let mensaje = '';
    if (!sucursalId) mensaje = 'Selecciona una sucursal';
    else if (!mpStoreId) mensaje = 'La sucursal no tiene MP Store ID';
    else mensaje = 'Selecciona un rango de fechas';
    
    return (
      <CardExpandible title="Conciliación Mercado Pago" icon={PaymentIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {mensaje}
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  if (error) {
    return (
      <CardExpandible title="Conciliación Mercado Pago" icon={PaymentIcon}>
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </CardExpandible>
    );
  }

  if (!conciliacion) {
    return (
      <CardExpandible title="Conciliación Mercado Pago" icon={PaymentIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No hay datos de conciliación disponibles
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  const { coincidencias, soloDB, soloMP } = conciliacion;

  // Datos para las cards de filtro
  const tabData = [
    { 
      label: 'Coincidencias', 
      count: coincidencias?.length || 0, 
      icon: getTabIcon(0)
    },
    { 
      label: 'Sistema', 
      count: soloDB?.length || 0, 
      icon: getTabIcon(1)
    },
    { 
      label: 'MP', 
      count: soloMP?.length || 0, 
      icon: getTabIcon(2)
    },
    { 
      label: 'Diferencia', 
      icon: getTabIcon(3)
    }
  ];

  return (
    <CardExpandible 
      title="Conciliación Mercado Pago" 
      icon={PaymentIcon}
      subtitle={sucursalNombre ? `Sucursal: ${sucursalNombre} · ` : ''}
    >
      <Box>

        {/* Cards clickeables en lugar de Tabs - 4 columnas */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {tabData.map((tab, index) => (
            <Grid item xs={3} key={index}>
              <Paper
                elevation={selectedTab === index ? 3 : 0}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  bgcolor: selectedTab === index ? getTabBgColor(index) : '#fafafa',
                  border: `2px solid ${selectedTab === index ? getTabBorderColor(index) : '#e0e0e0'}`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: selectedTab === index ? getTabBgColor(index) : '#f5f5f5',
                    transform: 'translateY(-2px)',
                    boxShadow: 2
                  },
                  '&:active': {
                    transform: 'scale(0.98)'
                  }
                }}
                onClick={() => handleTabChange(index)}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* {tab.icon} */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: selectedTab === index ? 600 : 400,
                        color: selectedTab === index ? getTabTextColor(index) : '#424242'
                      }}
                    >
                      {tab.label}
                    </Typography>
                  </Box>
                  {tab.count !== undefined && (
                    <Chip
                      label={tab.count}
                      size="small"
                      sx={{
                        bgcolor: selectedTab === index ? getTabBorderColor(index) : '#e0e0e0',
                        color: selectedTab === index ? '#ffffff' : '#424242',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        minWidth: 24,
                        height: 22
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Contenido según el tab seleccionado */}
        {selectedTab === 3 ? (
          // Tab de Diferencia - Mostrar en grande
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            bgcolor: '#f5f9ff',
            borderRadius: 2,
            border: '2px solid #1976d2',
            minHeight: 300,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Diferencia Total
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: Math.abs(resumen?.diferencia || 0) > 1000 ? '#d32f2f' : '#1976d2',
                mb: 2
              }}
            >
              ${Math.abs(resumen?.diferencia || 0).toLocaleString()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Total Sistema
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                  ${resumen?.totalDB ? formatNumber(resumen.totalDB) : '0'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Total Mercado Pago
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  ${resumen?.totalMP ? formatNumber(resumen.totalMP) : '0'}
                </Typography>
              </Box>
            </Box>
            {metadata && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="textSecondary">
                  Sucursal ID: {metadata.sucursalId} · MP Store ID: {metadata.mpStoreId}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
                  Procesado: {new Date(metadata.fechaProcesamiento).toLocaleString()}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Tablas para los otros tabs
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e8e8e8' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Referencia</TableCell>
                    <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Fecha</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Monto Sistema</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Monto MP</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datosPaginados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                          No hay registros para mostrar
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    datosPaginados.map((item, index) => {
                      const globalIndex = page * rowsPerPage + index;
                      const key = `${selectedTab}-${globalIndex}`;
                      const isCoincidencia = selectedTab === 0;
                      const isSoloDB = selectedTab === 1;
                      const isSoloMP = selectedTab === 2;
                      
                      const dbData = isCoincidencia ? item.db : (isSoloDB ? item : null);
                      const mpData = isCoincidencia ? item.mp : (isSoloMP ? item : null);
                      
                      return (
                        <React.Fragment key={key}>
                          <TableRow hover>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {mpData?.external_reference || dbData?.detalle || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {dbData?.fecha ? moment(dbData.fecha).format("DD-MM-YYYY HH:mm") : 
                               mpData?.date_created ? moment(mpData.date_created).format("DD-MM-YYYY HH:mm") : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                              {dbData?.monto ? `$${formatNumber(dbData.monto)}` : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                              {mpData?.transaction_amount ? `$${formatNumber(mpData.transaction_amount)}` : '-'}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                onClick={() => toggleExpandRow(key)}
                                sx={{ color: '#757575' }}
                              >
                                {expandedRows[key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 0 }}>
                              <Collapse in={expandedRows[key]} timeout="auto" unmountOnExit>
                                <Box sx={{ 
                                  p: 1.5, 
                                  bgcolor: '#fafafa',
                                  borderTop: '1px solid #e8e8e8'
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ mt: 0.5, minWidth: 30 }}>
                                      {isCoincidencia && (
                                        item.coincide ? 
                                          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} /> :
                                          <WarningIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                                      )}
                                      {isSoloDB && (
                                        <InfoIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                                      )}
                                      {isSoloMP && (
                                        <ErrorIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                                      )}
                                    </Box>

                                    <Box sx={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: '1fr 1fr',
                                      gap: '2px 16px',
                                      flex: 1,
                                      fontSize: '0.8rem'
                                    }}>
                                      <Box>
                                        {dbData?.pedido && (
                                          <Box sx={{ display: 'flex', gap: 0.5, py: 0.25 }}>
                                            <span style={{ fontWeight: 600, color: '#424242', minWidth: 65 }}>Pedido:</span>
                                            <span>{dbData.pedido}</span>
                                          </Box>
                                        )}
                                        {dbData?.cliente && (
                                          <Box sx={{ display: 'flex', gap: 0.5, py: 0.25 }}>
                                            <span style={{ fontWeight: 600, color: '#424242', minWidth: 65 }}>Cliente:</span>
                                            <span>{dbData.cliente}</span>
                                          </Box>
                                        )}
                                        {dbData?.chofer && (
                                          <Box sx={{ display: 'flex', gap: 0.5, py: 0.25 }}>
                                            <span style={{ fontWeight: 600, color: '#424242', minWidth: 65 }}>Chofer:</span>
                                            <span>{dbData.chofer}</span>
                                          </Box>
                                        )}
                                        {dbData?.detalle && (
                                          <Box sx={{ display: 'flex', gap: 0.5, py: 0.25 }}>
                                            <span style={{ fontWeight: 600, color: '#424242', minWidth: 65 }}>Detalle:</span>
                                            <span>{dbData.detalle}</span>
                                          </Box>
                                        )}
                                      </Box>

                                      <Box>
                                        {mpData?.id && (
                                          <Box sx={{ display: 'flex', gap: 0.5, py: 0.25 }}>
                                            <span style={{ fontWeight: 600, color: '#424242', minWidth: 65 }}>ID MP:</span>
                                            <span>{mpData.id}</span>
                                          </Box>
                                        )}
                                        {mpData?.payer?.identification?.number && (
                                          <Box sx={{ display: 'flex', gap: 0.5, py: 0.25 }}>
                                            <span style={{ fontWeight: 600, color: '#424242', minWidth: 65 }}>DNI/CUIT:</span>
                                            <span>{mpData.payer.identification.number}</span>
                                          </Box>
                                        )}
                                        {mpData?.payer?.email && (
                                          <Box sx={{ display: 'flex', gap: 0.5, py: 0.25 }}>
                                            <span style={{ fontWeight: 600, color: '#424242', minWidth: 65 }}>Email:</span>
                                            <span>{mpData.payer.email}</span>
                                          </Box>
                                        )}
                                        {mpData?.date_approved && (
                                          <Box sx={{ display: 'flex', gap: 0.5, py: 0.25 }}>
                                            <span style={{ fontWeight: 600, color: '#424242', minWidth: 65 }}>Aprobado:</span>
                                            <span>{moment(mpData.date_approved).format("DD-MM-YYYY HH:mm")}</span>
                                          </Box>
                                        )}
                                      </Box>       
                                    </Box>
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={7} />
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      colSpan={7}
                      count={datosActuales.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      labelRowsPerPage="Filas por página:"
                      labelDisplayedRows={({ from, to, count }) => 
                        `${from}-${to} de ${count}`
                      }
                      sx={{
                        '.MuiTablePagination-select': {
                          fontSize: '0.8rem'
                        },
                        '.MuiTablePagination-displayedRows': {
                          fontSize: '0.8rem'
                        }
                      }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            {metadata && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="textSecondary">
                  Sucursal ID: {metadata.sucursalId} · MP Store ID: {metadata.mpStoreId}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Procesado: {new Date(metadata.fechaProcesamiento).toLocaleString()}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </CardExpandible>
  );
};

export default CardPagosMercadoPago;